import React, { useContext, useEffect, useRef, useState } from 'react';
import { CircularProgress, Grid, MenuItem, Select } from '@material-ui/core';

import { fbmApi as api } from 'api/fbm';
import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FBMAccountPicker, Notifications } from 'shared';

import { FBMItemLotDetails } from './FBMInventoryLotDetails';

const ACCOUNTS_WITHOUT_RISK_COLUMNS = new Set(['blue_apron', 'marley_spoon']);

function FBMInventory() {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [facilityID, setFacilityID] = useState(null);
    const { fbmAccount, user } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [facilities, setFacilities] = useState([]);
    const [facilitiesAccountId, setFacilitiesAccountId] = useState(null);
    const inventoryRequestIdRef = useRef(0);
    const showRiskColumns = !ACCOUNTS_WITHOUT_RISK_COLUMNS.has(fbmAccount?.fbmAccountName);

    const handleAtRisk = (numUnits, inventoryRiskThreshold) => {
        if (!inventoryRiskThreshold) {
            return '';
        }

        if (numUnits <= inventoryRiskThreshold) {
            return 'AT RISK';
        }

        return '';
    };

    const getFbmInventory = async (currentFacilityId, accountName) => {
        const inventoryResponse = await api.getInventoryV2(currentFacilityId, accountName);
        if (!inventoryResponse.status) {
            return { error: inventoryResponse.msg, rows: [] };
        }

        return {
            rows: (inventoryResponse.data?.rows ?? []).map((row) => ({
                ...row,
                risk: handleAtRisk(row.availableUnits, row.inventoryRiskThreshold),
                facilityID: row.facilityId,
                mmSku: row.externalSku ?? row.packagingId ?? null,
            })),
        };
    };

    const getRows = async (currentFacilityId, accountName) => {
        const requestId = inventoryRequestIdRef.current + 1;
        inventoryRequestIdRef.current = requestId;
        setLoading(true);

        try {
            const { error, rows: allRows } = await getFbmInventory(
                currentFacilityId === 'ALL' ? null : currentFacilityId,
                accountName,
            );
            if (inventoryRequestIdRef.current !== requestId) {
                return;
            }
            if (error) {
                setNotification({ text: `Failed to get inventory for facility: ${error}`, level: 'error' });
                setRows([]);
                return;
            }
            setRows(allRows);
        } catch (error) {
            if (inventoryRequestIdRef.current !== requestId) {
                return;
            }
            setNotification({ text: `Error fetching inventory data: ${error.message}`, level: 'error' });
            setRows([]);
        } finally {
            if (inventoryRequestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!!user.id && user.facilityID) {
            setFacilityID(user.facilityID);
        }
    }, [user]);

    useEffect(() => {
        if (!fbmAccount?.id) {
            return undefined;
        }

        let cancelled = false;

        inventoryRequestIdRef.current += 1;
        setFacilityID('ALL');
        setFacilities([]);
        setFacilitiesAccountId(null);
        setRows([]);
        setLoading(true);

        const loadFacilities = async () => {
            try {
                const res = await warehouseApi.getFacilities(true);
                if (cancelled) {
                    return;
                }

                if (res.status !== true) {
                    setNotification({
                        text: `Failed to load facilities: ${res.msg || 'Unknown error'}`,
                        level: 'error',
                    });
                    setLoading(false);
                    return;
                }

                let fac = res.data.rows.map((item) => ({
                    text: item.name,
                    value: item.id,
                    active: item.active,
                }));

                if (fbmAccount?.facilityIDs?.length > 0) {
                    fac = fac.filter((item) => fbmAccount.facilityIDs.includes(item.value));
                }

                const facilitiesWithAll = [{ text: 'ALL FACILITIES', value: 'ALL', active: true }, ...fac];

                if (cancelled) {
                    return;
                }

                setFacilities(facilitiesWithAll);
                setFacilitiesAccountId(fbmAccount.id);
            } catch (error) {
                if (!cancelled) {
                    setNotification({
                        text: `Failed to load facilities: ${error.message || 'Unknown error'}`,
                        level: 'error',
                    });
                    setLoading(false);
                }
            }
        };

        loadFacilities();

        return () => {
            cancelled = true;
        };
        // Reload facilities when the selected FBM account changes.
        // facilityIDs are read from the account for this render.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed by account id
    }, [fbmAccount.id]);

    useEffect(() => {
        if (!!facilityID && !!fbmAccount.id && facilities.length > 0 && facilitiesAccountId === fbmAccount.id) {
            getRows(facilityID, fbmAccount.fbmAccountName);
        }
    }, [facilityID, fbmAccount.id, fbmAccount.fbmAccountName, facilities, facilitiesAccountId]);

    const exportDetailedData = async () => {
        try {
            const exportFacilityId = facilityID === 'ALL' ? null : facilityID;
            const response = await api.exportInventoryV2(exportFacilityId, fbmAccount.fbmAccountName);
            if (!response.status) {
                setNotification({
                    text: `Failed to export inventory: ${response.msg || 'Unknown error'}`,
                    level: 'error',
                });
            }
        } catch (error) {
            setNotification({
                text: `Failed to export inventory: ${error.message || 'Unknown error'}`,
                level: 'error',
            });
        }
    };

    const cols = [
        { accessor: 'facilityName', Header: 'Facility' },
        showRiskColumns ? { accessor: 'id', Header: 'Item ID' } : { accessor: 'mmSku', Header: 'MM Sku' },
        { accessor: 'type', Header: 'Type' },
        { accessor: 'name', Header: 'Name' },
        ...(showRiskColumns
            ? [
                  {
                      accessor: 'inventoryRiskThreshold',
                      Header: 'Risk Threshold',
                      editable: true,
                      editProps: { type: 'input', inputType: 'number', min: 0, integer: true },
                  },
                  { accessor: 'risk', Header: 'Risk' },
              ]
            : []),
        { Header: 'On-Hand Units', accessor: 'onHandUnits' },
        { Header: 'Order Allocated Units', accessor: 'onOrderUnits' },
        { Header: 'QC Audit Units', accessor: 'qcUnits' },
        { Header: 'Available Units', accessor: 'availableUnits' },
    ];

    return (
        <Grid container>
            <Notifications options={notification} />
            <Grid item xs={12}>
                <FBMAccountPicker filterNonFCFBMAccounts />
            </Grid>
            {!!fbmAccount.id && (
                <>
                    <Grid item md={6} style={{ textAlign: 'left', paddingLeft: '40px' }}>
                        <h1>{fbmAccount.companyName} Inventory</h1>
                    </Grid>
                    <Grid
                        item
                        md={6}
                        style={{
                            textAlign: 'right',
                            paddingRight: '40px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, marginRight: 8 }}>Facility</h2>
                            <Select
                                id="Facility_select"
                                style={{
                                    minWidth: 120,
                                    width: 'auto',
                                    marginLeft: 8,
                                    marginRight: 8,
                                }}
                                value={facilityID || ''}
                                variant="outlined"
                                onChange={(event) => {
                                    setFacilityID(event.target.value);
                                }}
                            >
                                {facilities.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <DataTable
                                editable
                                expandable
                                ExpansionComponent={<FBMItemLotDetails />}
                                csvExport
                                csvExportFileName="inventory"
                                columns={cols}
                                data={rows}
                                toolbarActions={[
                                    {
                                        name: 'Export Detailed',
                                        action: async () => {
                                            await exportDetailedData();
                                            return false; // Don't clear selected rows
                                        },
                                        disabled: !rows || rows.length === 0,
                                    },
                                ]}
                                saveEdit={async ({ row, value }) => {
                                    const res = await api.updateItemInventoryRiskThreshold(
                                        fbmAccount.fbmAccountName,
                                        row.id,
                                        value,
                                        row.facilityId,
                                        row.type,
                                    );
                                    if (res.status === false) {
                                        setNotification({ text: 'Error updating Inventory', level: 'error' });
                                    } else {
                                        setNotification({ text: 'Inventory Updated!', level: 'success' });
                                        await getRows(facilityID, fbmAccount.fbmAccountName);
                                    }
                                    return res.status;
                                }}
                            />
                        )}
                    </Grid>
                </>
            )}
        </Grid>
    );
}

export { FBMInventory };
